import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchLinkedCall,
  fetchSpanIo,
  fetchSpanPromptLog,
  type LinkedCallResponse,
  type LinkedLlmCall,
  type LinkedSttCall,
  type LinkedTtsCall,
  type LlmPromptLog,
  type SpanIoResponse,
  type TimelineSpan,
} from '../../api/admin.js';
import { formatDurationMs, formatNumber, nanoToUsd, shortId } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';
import { GATED_CONTENT_PLACEHOLDER, isGatedModuleError } from '../../lib/privacy.js';

interface SpanDetailPaneProps {
  span: TimelineSpan;
  onClose: () => void;
}

const BADGE_KIND_MAP: Record<string, string> = {
  llm: 'b-llm',
  tts: 'b-tts',
  stt: 'b-stt',
  tool_call: 'b-tool',
};

function kindBadgeClass(kind: string): string {
  return BADGE_KIND_MAP[kind] ?? 'b-neutral';
}

export function SpanDetailPane({ span, onClose }: SpanDetailPaneProps): ReactElement {
  const linkedCallQuery = useQuery<LinkedCallResponse | null>({
    queryKey: ['admin', 'linked-call', span.id],
    queryFn: () => fetchLinkedCall(span.id),
    enabled: Boolean(span.callKind && span.callRefId),
    staleTime: 30_000,
  });

  const promptLogQuery = useQuery<LlmPromptLog | null>({
    queryKey: ['admin', 'span-prompt-log', span.id],
    queryFn: () => fetchSpanPromptLog(span.id),
    enabled: span.callKind === 'llm' || span.kind === 'llm',
    staleTime: 60_000,
  });

  // Input/output crudo del span: se trae automático (sin gate ni audit log).
  const ioQuery = useQuery<SpanIoResponse | null>({
    queryKey: ['admin', 'span-io', span.id],
    queryFn: () => fetchSpanIo(span.id),
    enabled: span.hasIo,
    staleTime: 60_000,
  });

  const copyToClipboard = async (value: string): Promise<void> => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // no-op
    }
  };

  return (
    <aside className="panel-detail" data-testid="span-detail">
      <div className="pd-head">
        <div className="pd-head-top">
          <span className={`badge ${kindBadgeClass(span.kind)}`}>{span.kind}</span>
          <span className={`badge ${span.isError ? 'b-error' : 'b-ok'} pd-status`}>
            {span.isError ? span.errorKind ?? 'error' : 'ok'}
          </span>
          <button
            type="button"
            className="pd-close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="span-detail-close"
          >
            ×
          </button>
        </div>
        <div className="pd-name">{span.name}</div>
        {span.parentId ? (
          <div className="pd-sub">parent · {shortId(span.parentId, 12)}</div>
        ) : null}
      </div>

      {/* ── Stats ─────────────────────────────────────────────── */}
      <div className="pd-section">
        <h4 className="pd-section-title">Stats</h4>
        <div className="pd-kv">
          <span className="k">started_at</span>
          <span className="v">{new Date(span.startedAt).toISOString()}</span>
          <span className="k">ended_at</span>
          <span className="v">{new Date(span.endedAt).toISOString()}</span>
          <span className="k">duration</span>
          <span className="v">{formatDurationMs(span.durationMs)}</span>
          <span className="k">cost</span>
          <span className="v">
            {(() => {
              const cost = preferRawCost(span.providerCostNano, span.costNano);
              return cost ? nanoToUsd(cost) : '—';
            })()}
          </span>
          <span className="k">sample_reason</span>
          <span className="v">{span.sampleReason ?? '—'}</span>
          <span className="k">span_id</span>
          <span
            className="v copyable"
            onClick={() => void copyToClipboard(span.id)}
            title="Click to copy"
          >
            {shortId(span.id, 16)}
          </span>
          <span className="k">parent_id</span>
          <span
            className="v copyable"
            onClick={() => (span.parentId ? void copyToClipboard(span.parentId) : undefined)}
          >
            {span.parentId ? shortId(span.parentId, 16) : '—'}
          </span>
        </div>
      </div>

      {/* ── Attributes ────────────────────────────────────────── */}
      {span.attributes && Object.keys(span.attributes).length > 0 ? (
        <div className="pd-section">
          <h4 className="pd-section-title">
            Attributes
            <button
              type="button"
              className="btn ghost"
              style={{ height: 20, fontSize: 10 }}
              onClick={() => void copyToClipboard(JSON.stringify(span.attributes, null, 2))}
            >
              copy
            </button>
          </h4>
          <pre
            className="pd-json"
            dangerouslySetInnerHTML={{ __html: highlightJson(span.attributes) }}
          />
        </div>
      ) : null}

      {/* ── Error payload ─────────────────────────────────────── */}
      <SpanErrorSection span={span} />

      {/* ── Linked call ───────────────────────────────────────── */}
      {span.callKind && span.callRefId ? (
        <div className="pd-section" data-testid="linked-call-section">
          <h4 className="pd-section-title">
            Linked call <span className="badge b-neutral">{span.callKind}_calls</span>
          </h4>
          {linkedCallQuery.isPending ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Cargando…
            </div>
          ) : linkedCallQuery.isError ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Error: {linkedCallQuery.error.message}
            </div>
          ) : !linkedCallQuery.data ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              No persistida.
            </div>
          ) : (
            <LinkedCallView data={linkedCallQuery.data} />
          )}
        </div>
      ) : null}

      {/* ── Input / Output ────────────────────────────────────── */}
      {span.hasIo ? (
        <div className="pd-section" data-testid="reveal-section">
          <h4 className="pd-section-title">Input / Output</h4>
          {ioQuery.isPending ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Cargando…
            </div>
          ) : ioQuery.isError ? (
            // 403 gated_module (módulo sensible) NO es un error: placeholder sobrio.
            isGatedModuleError(ioQuery.error) ? (
              <div className="tt-tert" style={{ fontSize: 11 }} data-testid="span-io-gated">
                🔒 {GATED_CONTENT_PLACEHOLDER}
              </div>
            ) : (
              <div className="tt-tert" style={{ fontSize: 11 }}>
                Error: {ioQuery.error.message}
              </div>
            )
          ) : !ioQuery.data ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Sin input/output persistido.
            </div>
          ) : (
            <>
              <RevealedIo
                label="Input"
                value={ioQuery.data.input}
                onCopy={() => void copyToClipboard(stringifyIo(ioQuery.data?.input))}
              />
              <div style={{ height: 12 }} />
              <RevealedIo
                label="Output"
                value={ioQuery.data.output}
                onCopy={() => void copyToClipboard(stringifyIo(ioQuery.data?.output))}
              />
            </>
          )}
        </div>
      ) : null}

      {/* ── Prompt & Response (LLM only) ──────────────────────── */}
      {span.callKind === 'llm' || span.kind === 'llm' ? (
        <div className="pd-section" data-testid="prompt-log-section">
          <h4 className="pd-section-title">
            Prompt & Response
            {promptLogQuery.data ? (
              <button
                type="button"
                className="btn ghost"
                style={{ height: 20, fontSize: 10 }}
                onClick={() => {
                  if (!promptLogQuery.data) return;
                  void copyToClipboard(
                    JSON.stringify(
                      {
                        systemPrompt: promptLogQuery.data.systemPrompt,
                        messages: promptLogQuery.data.messages,
                        responseText: promptLogQuery.data.responseText,
                      },
                      null,
                      2,
                    ),
                  );
                }}
              >
                copy
              </button>
            ) : null}
          </h4>
          {promptLogQuery.isPending ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Cargando…
            </div>
          ) : promptLogQuery.isError ? (
            // 403 gated_module (módulo sensible) NO es un error: placeholder sobrio.
            isGatedModuleError(promptLogQuery.error) ? (
              <div className="tt-tert" style={{ fontSize: 11 }} data-testid="span-prompt-gated">
                🔒 {GATED_CONTENT_PLACEHOLDER}
              </div>
            ) : (
              <div className="tt-tert" style={{ fontSize: 11 }}>
                Error: {promptLogQuery.error.message}
              </div>
            )
          ) : !promptLogQuery.data ? (
            <div className="tt-tert" style={{ fontSize: 11 }}>
              Prompt log no persistido para este turno.
            </div>
          ) : (
            <PromptLogView log={promptLogQuery.data} />
          )}
        </div>
      ) : null}

      {/* ── Token breakdown (LLM only) ────────────────────────── */}
      {span.callKind === 'llm' && linkedCallQuery.data && isLlmRow(linkedCallQuery.data.row) ? (
        <TokenBreakdown call={linkedCallQuery.data.row} />
      ) : null}
    </aside>
  );
}

interface PromptLogViewProps {
  log: LlmPromptLog;
}

function PromptLogView({ log }: PromptLogViewProps): ReactElement {
  return (
    <div className="pd-promptlog">
      <h5 className="pd-section-title" style={{ marginBottom: 6 }}>
        System Prompt
      </h5>
      <pre className="pd-json pd-promptlog-text">{log.systemPrompt || '—'}</pre>

      <h5 className="pd-section-title" style={{ marginTop: 12, marginBottom: 6 }}>
        Messages (input al LLM)
      </h5>
      <pre
        className="pd-json"
        dangerouslySetInnerHTML={{ __html: highlightJson(log.messages) }}
      />

      <h5 className="pd-section-title" style={{ marginTop: 12, marginBottom: 6 }}>
        Response
      </h5>
      <pre className="pd-json pd-promptlog-text">{log.responseText ?? '—'}</pre>
    </div>
  );
}

/** Serializa input/output para copiar: strings tal cual, resto como JSON. */
function stringifyIo(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

interface LlmRequestLike {
  system?: unknown;
  messages?: unknown;
  tools?: unknown;
  tool_choice?: unknown;
}

/**
 * Heurística para detectar el shape de un request LLM (lo que persiste el
 * destilado one-shot: `{ system, messages, tools, tool_choice }`). Cuando
 * matchea, lo renderizamos legible (texto con wrap) en vez de un JSON con el
 * system prompt + transcript aplastados en una sola línea.
 */
function asLlmRequest(value: unknown): LlmRequestLike | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.system === 'string' || Array.isArray(v.messages)) return v;
  return null;
}

function isContentBlockArray(value: unknown): value is Array<Record<string, unknown>> {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (b) => b !== null && typeof b === 'object' && typeof (b as { type?: unknown }).type === 'string',
    )
  );
}

/** Renderiza un string como texto con wrap, o un valor estructurado como JSON. */
function IoChunk({ value }: { value: unknown }): ReactElement {
  if (typeof value === 'string') {
    return <pre className="pd-json pd-promptlog-text">{value || '—'}</pre>;
  }
  return <pre className="pd-json" dangerouslySetInnerHTML={{ __html: highlightJson(value) }} />;
}

/** Lista de bloques (messages de un request, o content blocks de un response). */
function BlocksView({
  blocks,
  labelOf,
  bodyOf,
}: {
  blocks: Array<Record<string, unknown>>;
  labelOf: (b: Record<string, unknown>, i: number) => string;
  bodyOf: (b: Record<string, unknown>) => unknown;
}): ReactElement {
  return (
    <div className="pd-msgs">
      {blocks.map((b, i) => (
        <div className="pd-msg" key={i}>
          <div className="pd-msg-role">{labelOf(b, i)}</div>
          <IoChunk value={bodyOf(b)} />
        </div>
      ))}
    </div>
  );
}

/** Vista legible del cuerpo de input/output revelado. */
function IoBody({ value }: { value: unknown }): ReactElement {
  const req = asLlmRequest(value);
  if (req) {
    return (
      <div className="pd-promptlog">
        {typeof req.system === 'string' ? (
          <>
            <div className="pd-io-sublabel">system</div>
            <pre className="pd-json pd-promptlog-text">{req.system || '—'}</pre>
          </>
        ) : null}
        {Array.isArray(req.messages) ? (
          <>
            <div className="pd-io-sublabel">messages</div>
            <BlocksView
              blocks={req.messages.map((m) =>
                m !== null && typeof m === 'object' ? (m as Record<string, unknown>) : { content: m },
              )}
              labelOf={(m, i) => (typeof m.role === 'string' ? m.role : `#${i}`)}
              bodyOf={(m) => m.content}
            />
          </>
        ) : null}
        {req.tools !== undefined ? (
          <>
            <div className="pd-io-sublabel">tools</div>
            <pre className="pd-json" dangerouslySetInnerHTML={{ __html: highlightJson(req.tools) }} />
          </>
        ) : null}
      </div>
    );
  }
  if (isContentBlockArray(value)) {
    return (
      <BlocksView
        blocks={value}
        labelOf={(b) =>
          typeof b.name === 'string' ? `${String(b.type)} · ${b.name}` : String(b.type)
        }
        bodyOf={(b) => (typeof b.text === 'string' ? b.text : b.input ?? b)}
      />
    );
  }
  return <IoChunk value={value} />;
}

/** Sección Input u Output revelado, con botón de copiar. */
function RevealedIo({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: unknown;
  onCopy: () => void;
}): ReactElement {
  return (
    <div>
      <div className="pd-io-head">
        <h5 className="pd-section-title" style={{ margin: 0 }}>
          {label}
        </h5>
        <button
          type="button"
          className="btn ghost"
          style={{ height: 20, fontSize: 10 }}
          onClick={onCopy}
        >
          copy
        </button>
      </div>
      <IoBody value={value} />
    </div>
  );
}

function isLlmRow(row: LinkedLlmCall | LinkedTtsCall | LinkedSttCall): row is LinkedLlmCall {
  return 'inputTokens' in row && 'outputTokens' in row;
}

interface LinkedCallViewProps {
  data: LinkedCallResponse;
}

function LinkedCallView({ data }: LinkedCallViewProps): ReactElement {
  const row = data.row;
  if (data.callKind === 'llm' && isLlmRow(row)) {
    const total = row.inputTokens + row.outputTokens + row.cacheReadTokens + row.cacheCreateTokens;
    return (
      <div className="pd-kv">
        <span className="k">call_id</span>
        <span className="v">{shortId(row.id, 18)}</span>
        <span className="k">provider</span>
        <span className="v">{row.provider}</span>
        <span className="k">model</span>
        <span className="v">{row.model}</span>
        <span className="k">tokens</span>
        <span className="v">{formatNumber(total)}</span>
        <span className="k">latency_ms</span>
        <span className="v">{row.latencyMs ?? '—'}</span>
        <span className="k">ttft_ms</span>
        <span className="v">{row.ttftMs ?? '—'}</span>
        <span className="k">retries</span>
        <span className="v">{row.retries}</span>
        <span className="k">cost</span>
        <span className="v">{nanoToUsd(preferRawCost(row.providerCostNano, row.costNano) ?? '0')}</span>
      </div>
    );
  }
  if (data.callKind === 'tts' && 'characters' in row) {
    return (
      <div className="pd-kv">
        <span className="k">call_id</span>
        <span className="v">{shortId(row.id, 18)}</span>
        <span className="k">provider</span>
        <span className="v">{row.provider}</span>
        <span className="k">model</span>
        <span className="v">{row.model}</span>
        <span className="k">voice_id</span>
        <span className="v">{row.voiceId ?? '—'}</span>
        <span className="k">characters</span>
        <span className="v">{formatNumber(row.characters)}</span>
        <span className="k">audio_s</span>
        <span className="v">{row.audioSeconds ?? '—'}</span>
        <span className="k">latency_ms</span>
        <span className="v">{row.latencyMs ?? '—'}</span>
        <span className="k">cost</span>
        <span className="v">{nanoToUsd(preferRawCost(row.providerCostNano, row.costNano) ?? '0')}</span>
      </div>
    );
  }
  // stt
  const sttRow = row as LinkedSttCall;
  return (
    <div className="pd-kv">
      <span className="k">call_id</span>
      <span className="v">{shortId(sttRow.id, 18)}</span>
      <span className="k">provider</span>
      <span className="v">{sttRow.provider}</span>
      <span className="k">model</span>
      <span className="v">{sttRow.model}</span>
      <span className="k">audio_s</span>
      <span className="v">{sttRow.audioSeconds}</span>
      <span className="k">chars_out</span>
      <span className="v">{sttRow.charactersOut ?? '—'}</span>
      <span className="k">latency_ms</span>
      <span className="v">{sttRow.latencyMs ?? '—'}</span>
      <span className="k">cost</span>
      <span className="v">{nanoToUsd(preferRawCost(sttRow.providerCostNano, sttRow.costNano) ?? '0')}</span>
    </div>
  );
}

interface TokenBreakdownProps {
  call: LinkedLlmCall;
}

function TokenBreakdown({ call }: TokenBreakdownProps): ReactElement | null {
  const total =
    call.inputTokens + call.outputTokens + call.cacheReadTokens + call.cacheCreateTokens;
  if (total === 0) return null;
  const segments: Array<{ label: string; value: number; color: string }> = [
    { label: 'cache_read', value: call.cacheReadTokens, color: 'oklch(70% 0.18 145 / 0.7)' },
    { label: 'cache_write', value: call.cacheCreateTokens, color: 'oklch(72% 0.14 65 / 0.7)' },
    { label: 'input', value: call.inputTokens, color: 'var(--k-llm)' },
    { label: 'output', value: call.outputTokens, color: 'oklch(75% 0.2 290)' },
  ];
  return (
    <div className="pd-section" data-testid="token-breakdown">
      <h4 className="pd-section-title">Token breakdown</h4>
      <div className="pd-tokens-bar">
        {segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.label}
              style={{ flex: s.value, background: s.color }}
              title={`${s.label} ${s.value}`}
            />
          ) : null,
        )}
      </div>
      <div className="pd-tokens-legend">
        {segments.map((s) =>
          s.value > 0 ? (
            <span key={s.label}>
              <span className="sw" style={{ background: s.color }} />
              {s.label} {formatNumber(s.value)}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

/**
 * Sintaxis highlight básica para JSON. Reemplaza tokens con spans
 * (jk=key, js=string, jn=number, jb=bool/null). Sanitiza HTML antes
 * para evitar inyección.
 */
function highlightJson(value: unknown): string {
  const json = JSON.stringify(value, null, 2);
  const escaped = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(\.\d+)?([eE][+-]?\d+)?)/g,
    (match) => {
      if (/^"/.test(match)) {
        return /:$/.test(match) ? `<span class="jk">${match}</span>` : `<span class="js">${match}</span>`;
      }
      if (/true|false/.test(match)) return `<span class="jb">${match}</span>`;
      if (/null/.test(match)) return `<span class="jb">${match}</span>`;
      return `<span class="jn">${match}</span>`;
    },
  );
}

function extractErrorMessage(attrs: Record<string, unknown>): string | null {
  if (!attrs || typeof attrs !== 'object') return null;
  const candidates = ['error_message', 'message', 'error'];
  for (const key of candidates) {
    const v = attrs[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

interface ErrorInfo {
  name: string | null;
  message: string | null;
  stack: string | null;
}

/**
 * Extrae `{ name, message, stack }` del `error_payload` serializado del span
 * (`serializeError` de @orborix/tracing). El mensaje real vive acá — NO en
 * `attributes` (que sólo trae model/provider/etc.). Cae a `attributes` sólo
 * como fallback para spans viejos sin payload o instrumentaciones que no lo
 * serializan.
 */
function extractErrorInfo(payload: unknown, attrs: Record<string, unknown>): ErrorInfo {
  let name: string | null = null;
  let message: string | null = null;
  let stack: string | null = null;
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (typeof p['name'] === 'string' && p['name'].length > 0) name = p['name'];
    if (typeof p['message'] === 'string' && p['message'].length > 0) message = p['message'];
    if (typeof p['stack'] === 'string' && p['stack'].length > 0) stack = p['stack'];
  }
  if (!message) message = extractErrorMessage(attrs);
  return { name, message, stack };
}

/**
 * Sección "Error" del drawer de detalle de un span. Compartida entre el panel
 * post-mortem (`SpanDetailPane`) y el Live (`LiveSpanDetail`) para que ambos
 * muestren el mismo payload real. Devuelve `null` si el span no es error.
 */
export function SpanErrorSection({ span }: { span: TimelineSpan }): ReactElement | null {
  if (!span.isError) return null;
  const info = extractErrorInfo(span.errorPayload, span.attributes);
  return (
    <div className="pd-section" data-testid="span-error-section">
      <h4 className="pd-section-title">Error</h4>
      <div className="pd-err-card">
        <div className="pd-err-kind">
          {span.errorKind ?? 'error'}
          {info.name && info.name !== span.errorKind ? ` · ${info.name}` : ''}
        </div>
        <div className="tt-sec mono" style={{ fontSize: 11 }}>
          {info.message ?? '—'}
        </div>
        {info.stack ? (
          <details className="pd-err-stack" style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer', fontSize: 11 }}>stack</summary>
            <pre
              className="pd-json pd-promptlog-text"
              style={{ fontSize: 10, whiteSpace: 'pre-wrap' }}
            >
              {info.stack}
            </pre>
          </details>
        ) : null}
      </div>
    </div>
  );
}
